import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    updateDoc,
} from "firebase/firestore";

const Services = () => {
    const [services, setServices] = useState([]);
    const [categoryName, setCategoryName] = useState("");
    // Stored as `color` but read by the apps as isExclusive ("yes" | "no").
    const [categoryColor, setCategoryColor] = useState("no");
    const [categoryImage, setCategoryImage] = useState("");
    const [categoryPriority, setCategoryPriority] = useState("");
    const [categoryDescription, setCategoryDescription] = useState("");
    const [editingService, setEditingService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [options, setOptions] = useState([]);

    const [optionTitle, setOptionTitle] = useState("");
    const [optionDescription, setOptionDescription] = useState("");
    const [optionprices, setOptionprices] = useState("");
    const [optionRecommended, setOptionRecommended] = useState(false);


    const fetchServices = async () => {
        try {
            const servicesCollection = collection(db, "Categories");
            const serviceSnapshot = await getDocs(servicesCollection);
            const servicesList = serviceSnapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
            }));

            // sort by priority (smaller number = higher rank)
            servicesList.sort(
                (a, b) => (a.priority ?? 999) - (b.priority ?? 999)
            );

            setServices(servicesList);
        } catch (err) {
            console.error(err);
            setError("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!categoryName || !categoryImage || !categoryDescription.trim() || categoryPriority === "") {
            setError("All fields are required, including priority.");
            return;
        }

        try {
            const categoryCollection = collection(db, "Categories");
            const docRef = await addDoc(categoryCollection, {
                name: categoryName,
                color: categoryColor === "yes" ? "yes" : "no",
                image: categoryImage,
                priority: Number(categoryPriority),
                description: categoryDescription.trim(),
            });

            const categoryDoc = doc(db, "Categories", docRef.id);
            await updateDoc(categoryDoc, {
                id: docRef.id,
            });

            setCategoryName("");
            setCategoryColor("no");
            setCategoryImage("");
            setCategoryPriority("");
            setCategoryDescription("");
            setError("");
            fetchServices();
        } catch (err) {
            console.error(err);
            setError("Failed to add category");
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            const categoryDoc = doc(db, "Categories", categoryId);
            await deleteDoc(categoryDoc);
            fetchServices();
        } catch (err) {
            console.error(err);
            setError("Failed to delete category");
        }
    };

    const handleEditCategory = async (e) => {
        e.preventDefault();
        if (
            !editingService.name ||
            !editingService.image ||
            !editingService.description ||
            editingService.priority === "" ||
            editingService.priority === undefined
        ) {
            setError("All fields are required, including priority.");
            return;
        }

        try {
            const categoryDoc = doc(db, "Categories", editingService.id);
            await updateDoc(categoryDoc, {
                name: editingService.name,
                color: editingService.color === "yes" ? "yes" : "no",
                image: editingService.image,
                priority: Number(editingService.priority),
                description: (editingService.description || "").trim(),
            });

            setEditingService(null);
            setError("");
            fetchServices();
        } catch (err) {
            console.error(err);
            setError("Failed to edit category");
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingService((prev) => ({
            ...prev,
            [name]: name === "priority" ? value : value,
        }));
    };

    if (loading) return <p className="p-4">Loading categories...</p>;

    return (
        <div className="h-screen overflow-y-auto p-4 bg-gray-50">
            <h1 className="text-3xl font-bold mb-6">Categories</h1>
            {error && <p className="text-red-600 mb-4">{error}</p>}

            {/* Add or Edit Form */}
            <form
                onSubmit={editingService ? handleEditCategory : handleAddCategory}
                className="bg-white p-4 shadow rounded mb-6"
            >
                <h2 className="text-lg font-semibold mb-4">
                    {editingService
                        ? `Edit Category: ${editingService.name}`
                        : "Add Category"}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                        Name
                        <input
                            type="text"
                            name="name"
                            placeholder="e.g. Plumber"
                            className="p-2 border rounded font-normal"
                            value={editingService ? editingService.name : categoryName}
                            onChange={(e) =>
                                editingService
                                    ? handleEditChange(e)
                                    : setCategoryName(e.target.value)
                            }
                        />
                    </label>

                    <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                        Priority
                        <input
                            type="number"
                            name="priority"
                            placeholder="0 = top"
                            className="p-2 border rounded font-normal"
                            value={
                                editingService
                                    ? editingService.priority ?? ""
                                    : categoryPriority
                            }
                            onChange={(e) =>
                                editingService
                                    ? handleEditChange(e)
                                    : setCategoryPriority(e.target.value)
                            }
                        />
                    </label>

                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            name="color"
                            checked={
                                (editingService ? editingService.color : categoryColor) === "yes"
                            }
                            onChange={(e) => {
                                const value = e.target.checked ? "yes" : "no";
                                editingService
                                    ? setEditingService((prev) => ({ ...prev, color: value }))
                                    : setCategoryColor(value);
                            }}
                        />
                        Is exclusive service
                    </label>

                    <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                        Image URL
                        <input
                            type="text"
                            name="image"
                            placeholder="https://…"
                            className="p-2 border rounded font-normal"
                            value={editingService ? editingService.image : categoryImage}
                            onChange={(e) =>
                                editingService
                                    ? handleEditChange(e)
                                    : setCategoryImage(e.target.value)
                            }
                        />
                    </label>

                    <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 md:col-span-2">
                        Description
                        <textarea
                            name="description"
                            placeholder="What this service covers"
                            className="p-2 border rounded font-normal min-h-24"
                            value={
                                editingService
                                    ? editingService.description ?? ""
                                    : categoryDescription
                            }
                            onChange={(e) =>
                                editingService
                                    ? handleEditChange(e)
                                    : setCategoryDescription(e.target.value)
                            }
                        />
                    </label>
                </div>

                <div className="flex gap-2 mt-4">
                    <button
                        type="submit"
                        className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                    >
                        {editingService ? "Update Category" : "Add Category"}
                    </button>
                    {editingService && (
                        <button
                            type="button"
                            onClick={() => {
                                setEditingService(null);
                                setError("");
                            }}
                            className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            {/* Categories List */}
            <div className="grid gap-4">
                {services.map((category) => (
                    <div
                        key={category.id}
                        className="flex flex-wrap sm:flex-nowrap justify-between items-center bg-white p-4 shadow rounded gap-4"
                    >
                        <div className="flex items-center gap-4 flex-1">
                            {category.image && (
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-12 h-12 object-contain"
                                />
                            )}
                            <div className="space-y-1 overflow-hidden">
                                <h3 className="font-semibold text-lg break-words">
                                    {category.name}
                                </h3>
                                {category.description && (
                                    <p className="text-sm text-gray-600 whitespace-pre-line">
                                        {category.description}
                                    </p>
                                )}
                                <p className="text-sm">
                                    Exclusive: {category.color === "yes" ? "Yes" : "No"}
                                </p>
                                <p className="text-sm">
                                    Priority: {category.priority ?? "Not set"}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-xs">
                                    ID: {category.id}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => {
                                    setSelectedCategory(category);
                                    setOptions(category.options || []);
                                }}
                                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            >
                                Options
                            </button>

                            <button
                                onClick={() => setEditingService(category)}
                                className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>{/* OPTIONS MANAGEMENT */}
            {selectedCategory && (
                <div className="bg-white p-4 shadow rounded mt-6">
                    <h2 className="text-xl font-bold mb-4">
                        Options for {selectedCategory.name}
                    </h2>

                    {/* Add Option */}
                    <div className="grid gap-2 mb-4">
                        <input
                            type="text"
                            placeholder="Option Title"
                            className="p-2 border rounded"
                            value={optionTitle}
                            onChange={(e) => setOptionTitle(e.target.value)}
                        />

                        <textarea
                            placeholder="Description"
                            className="p-2 border rounded"
                            value={optionDescription}
                            onChange={(e) => setOptionDescription(e.target.value)}
                        />

                        <input
                            type="number"
                            placeholder="prices"
                            className="p-2 border rounded"
                            value={optionprices}
                            onChange={(e) => setOptionprices(e.target.value)}
                        />

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={optionRecommended}
                                onChange={(e) => setOptionRecommended(e.target.checked)}
                            />
                            isRecommended
                        </label>

                        <button
                            onClick={() => {
                                if (!optionTitle || !optionDescription || optionprices === "") return;

                                setOptions([
                                    ...options,
                                    {
                                        title: optionTitle,
                                        description: optionDescription,
                                        prices: Number(optionprices),
                                        isRecommended: optionRecommended,
                                    },
                                ]);

                                setOptionTitle("");
                                setOptionDescription("");
                                setOptionprices("");
                                setOptionRecommended(false);
                            }}
                            className="bg-blue-600 text-white py-2 rounded"
                        >
                            Add Option
                        </button>
                    </div>

                    {/* Options List */}
                    <div className="space-y-2">
                        {options.map((opt, index) => (
                            <div
                                key={index}
                                className="border p-3 rounded flex justify-between"
                            >
                                <div>
                                    <p className="font-semibold">
                                        {opt.title}
                                        {opt.isRecommended && (
                                            <span className="ml-2 text-green-600 text-sm">
                                                (isRecommended)
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-sm">{opt.description}</p>
                                    <p className="text-sm">₹{opt.prices}</p>
                                </div>

                                <button
                                    onClick={() =>
                                        setOptions(options.filter((_, i) => i !== index))
                                    }
                                    className="text-red-600"
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        className="mt-4 bg-green-700 text-white px-4 py-2 rounded"
                        onClick={async () => {
                            try {
                                const categoryDoc = doc(db, "Categories", selectedCategory.id);
                                await updateDoc(categoryDoc, { options });
                                setSelectedCategory(null);
                                setOptions([]);
                                fetchServices();
                            } catch (e) {
                                alert("Failed to save options");
                            }
                        }}
                    >
                        Save Options
                    </button>
                </div>
            )}

        </div>
    );
};

export default Services;
